import React, {useCallback} from 'react';
import {classes, style, vars} from './doppe-standard-action-button-template.st.css';
import type {DoppeActionView} from '../../../doppe-sdk';
import {doppeActionButtonTemplateDefine} from '../../../doppe-sdk';
import {DoppeViewerActionView} from '../../doppe-viewer-action-view/doppe-viewer-action-view';
import {
    Button,
    Icon,
    ImageViewPositionHorizontal,
    ImageViewPositionVertical,
    ImageViewResizeMode,
    reactCreateMemo,
    useHashedMemoForRecord,
} from '@wix/devzai-utils-react';
import IconChevronDown from './icons/ChevronDown.svg';
import {FlexLayout} from '@wix/devzai-common-client';
import type {DoppeStandardActionButtonTemplate} from './doppe-standard-action-button-template.metadata';
import {
    DoppeStandardActionButtonTemplateMetadata
} from './doppe-standard-action-button-template.metadata'
import {DoppePageMediaView, DoppePageMediaViewMediaBackground} from '../../doppe-page-media-view/doppe-page-media-view';
import {
    evaluateFunction,
    HtmlObjectHorizontalPosition,
    HtmlObjectResizeMode,
    HtmlObjectVerticalPosition,
    stringReplaceEmptyOrWhiteSpaceValue, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import {DoppeDtoActionViewType} from '../../../client-server-common/types/doppe-dto-action';
import {DoppeViewerBannerDefaultMediaResource} from '../../../doppe-static-assets/doppe-static-assets';
import {
    cssBymoPageFontSizePageButtonDescription,
    cssBymoPageFontSizePageButtonTitle
} from "../../bymo-page-style/bymo-page-style";
import {useDoppeStandardActionButtonChromeStyle} from './doppe-standard-action-button-template-chrome-style';

const DoppeStandardActionButton = reactCreateMemo(function DoppeStandardActionButton(props: DoppeActionView.Props<DoppeStandardActionButtonTemplate.TemplateProps>) {
    const {
        templateProps,
        forcedBymoPageStyleScope,
        enableAnimation = false,
        viewType,
        actionIndexInList,
        isLockedObservable,

        ...actionViewProps
    } = props;

    const chromeStyle = useDoppeStandardActionButtonChromeStyle({
        templateProps,
        actionIndexInList,
    });

    const {roundness, skin, hoverAnimation} = chromeStyle;

    const styleProp = useHashedMemoForRecord({
        [vars.roundness]: `${roundness}px`,
        [vars.blendedMainColor]: chromeStyle.blendedMainColor ?? undefined,
        [vars.buttonColor]: chromeStyle.buttonColor,
        [vars.mainColorRgb]: chromeStyle.mainColorRgb ?? undefined,
        [vars.gradientStart]: chromeStyle.gradientStart ?? undefined,
        [vars.gradientEnd]: chromeStyle.gradientEnd ?? undefined,
        [vars.gradientLegibleForeground]: chromeStyle.gradientLegibleForeground ?? undefined,
    });

    const renderActionButton = useCallback<DoppeViewerActionView.Props['renderActionButton']>((renderProps) => {

        const {
            actionButtonComponentProps: {
                className,
                href,
                ...htmlAttributes
            },
            actionTitle,
            actionDescription,
            actionImage,
            actionViewType,
            bannerMedia,
            isLocked,

            isWidgetOpener
        } = renderProps;

        const hasImage = hideableValueIsVisibleAndNotEqualValue(actionImage, null) && actionImage !== null;
        const visibleActionDescription = stringReplaceEmptyOrWhiteSpaceValue(hideableValueGetVisibleValue(actionDescription, undefined), undefined);

        return (
            <Button
                {...htmlAttributes}
                className={style(
                    classes.actionButton,
                    {
                        hasImage: hasImage,
                        hasCustomContent: actionViewType === DoppeDtoActionViewType.BannerButton
                    },
                    className
                )}
                tagName={!isLocked && href !== undefined ? 'a' : 'button'}
                href={isLocked ? undefined : href}
            >
                {evaluateFunction(() => {
                    switch (actionViewType) {
                        case DoppeDtoActionViewType.BannerButton: {
                            return (
                                <DoppePageMediaView
                                    className={style(classes.bannerMediaView, className)}
                                    mediaResource={bannerMedia ?? DoppeViewerBannerDefaultMediaResource}
                                    layoutSpec={{autoHeight: true, maxHeight: 860}}
                                    widthSpec={{contentRelativeWidth: 1}}
                                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                                    mediaVerticalPosition={HtmlObjectVerticalPosition.Center}
                                    mediaHorizontalPosition={HtmlObjectHorizontalPosition.Center}
                                    videoMuted={true}
                                    autoPlayVideo={true}
                                    playVideoInLoop={true}
                                />
                            )
                        }
                        default: {
                            return (
                                <>
                                    {hasImage ? (
                                        <DoppePageMediaView
                                            className={style(classes.actionImageView, {})}
                                            mediaResource={actionImage}
                                            layoutSpec={{widthHeightRatio: 1}}
                                            widthSpec={{width: 44}}
                                            mediaVerticalPosition={ImageViewPositionVertical.Center}
                                            mediaHorizontalPosition={ImageViewPositionHorizontal.Center}
                                            mediaResizeMode={ImageViewResizeMode.Cover}
                                            mediaBackground={DoppePageMediaViewMediaBackground.Transparent}
                                        />
                                    ) : null}
                                    <div
                                        className={style(classes.actionTextContent, {}, FlexLayout.column.default, FlexLayout.fillRemainingHorizontalSpace)}
                                    >
                                        <h2
                                            className={style(classes.actionTitle, cssBymoPageFontSizePageButtonTitle())}
                                        >
                                            {actionTitle}
                                        </h2>
                                        {visibleActionDescription ? (
                                            <div
                                                className={style(classes.actionDescription, cssBymoPageFontSizePageButtonDescription())}
                                            >
                                                {visibleActionDescription}
                                            </div>
                                        ) : null}
                                    </div>
                                    {evaluateFunction(() => {

                                        if (isWidgetOpener) {
                                            return (
                                                <Icon
                                                    className={style(classes.chevronIcon, {})}
                                                    icon={IconChevronDown}
                                                />
                                            )
                                        }

                                        return null;

                                    })
                                    }
                                </>
                            )
                        }
                    }
                })}

            </Button>
        )

    }, [])

    return (
        <DoppeViewerActionView
            className={style(classes.root, {
                enableAnimation: enableAnimation,
                skin: skin,
                hoverAnimation: hoverAnimation,
                viewType: evaluateFunction(() => {
                    switch (viewType) {
                        case DoppeDtoActionViewType.InPageFrameless: return 'strip';
                        case DoppeDtoActionViewType.ActionButton: return 'button';
                        case DoppeDtoActionViewType.InPage: return 'card';
                        case DoppeDtoActionViewType.BannerButton: return 'banner';
                    }
                })
            })}
            actionWidgetContainerClassName={classes.actionWidgetContainer}
            actionButtonContainerClassName={classes.actionButtonContainer}
            actionWidgetRoundness={roundness}
            actionIndexInList={actionIndexInList}
            renderActionButton={renderActionButton}
            style={styleProp}
            viewType={viewType}
            isLockedObservable={isLockedObservable}
            forcedBymoPageStyleScope={forcedBymoPageStyleScope}
            {...actionViewProps}
        />
    )
});

export const DoppeStandardActionButtonTemplateData = doppeActionButtonTemplateDefine(
    DoppeStandardActionButtonTemplateMetadata,
    DoppeStandardActionButton)